import axios, { AxiosResponse } from 'axios';
import { CommentTypeId, IComment } from '../../../components/Comments/Comments.types';
import {
  AnnotationOnImage,
  AnnotationsCriterionDTO,
  AnnotationsForORGZ,
  AnnotationVersionsForORGZ,
} from '../../../models/annotation.types';
import {
  deleteScoringState,
  deleteSheetState,
  deleteState,
  ExamTaskGroupSolutionScoreShelfResponse,
  ExamTaskGroupSolutionWithTasks,
} from '../../../models/assessment.types';
import EnvWindow from '../../../utils/env';
import { SendBackAssessedExamTaskGroupSolutionsResponse } from '../../scoring/scoring.types';

export async function getExamTaskGroup(): Promise<ExamTaskGroupSolutionWithTasks> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup`);
  return response.data;
}

export async function deleteExamTaskGroup(examTaskGroupId: string | null): Promise<deleteState> {
  const response = await axios.delete(
    examTaskGroupId !== null
      ? `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup/${examTaskGroupId}`
      : `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup`,
  );
  return response.data;
}

export async function deleteExamScoringSchema(examScoringSchemaId: string | null): Promise<deleteScoringState> {
  const response = await axios.delete(
    `${EnvWindow.REACT_APP_EXAMS__EXAM_SCORING_SCHEMA_API_URL}/${examScoringSchemaId}`,
  );
  return response.data;
}

export async function deleteExamSheet(examSheetId: string | null): Promise<deleteSheetState> {
  const response = await axios.delete(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}`);
  return response.data;
}

export async function deleteExamTask(examTaskId: string | null): Promise<deleteSheetState> {
  const response = await axios.delete(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTask/${examTaskId}`);
  return response.data;
}

export const getComments = async (
  orgzID: string,
  examTaskGroupSolutionScoreVersionId?: string,
): Promise<AxiosResponse<IComment[]>> => {
  const examTaskGroupSolutionScoreVersionIdPart = examTaskGroupSolutionScoreVersionId
    ? `?examTaskGroupSolutionScoreVersionId=${examTaskGroupSolutionScoreVersionId}`
    : '';
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${orgzID}/Comment${examTaskGroupSolutionScoreVersionIdPart}`,
  );
  return response;
};

export async function addComment(
  orgzID: string | undefined,
  commentTypeId: CommentTypeId,
  content: string,
  positionX: number | null = null,
  positionY: number | null = null,
  pageNumber: number | null = null,
  partialScoreId: string | null = null,
): Promise<AxiosResponse<boolean>> {
  const response = axios.put(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${orgzID}/Comment`,
    {
      commentTypeId,
      content,
      positionX: positionX !== null ? Math.round(positionX) : null,
      positionY: positionY !== null ? Math.round(positionY) : null,
      pageNumber,
      partialScoreId,
    },
  );
  return response;
}

export async function updateComment(
  commentId: string | undefined,
  content: string,
  positionX: number | null = null,
  positionY: number | null = null,
  pageNumber: number | null = null,
  partialScoreId: string | null = null,
): Promise<AxiosResponse<boolean>> {
  const response = axios.patch(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/Comment/${commentId}`,
    {
      content,
      positionX: positionX !== null ? Math.round(positionX) : null,
      positionY: positionY !== null ? Math.round(positionY) : null,
      pageNumber,
      partialScoreId,
    },
  );
  return response;
}

export async function deleteComment(
  commentId: string,
  partialScoreId: string | null = null,
): Promise<AxiosResponse<boolean>> {
  const partialScoreIdPart = partialScoreId ? `?partialScoreId=${partialScoreId}` : '';
  const response = axios.delete(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/Comment/${commentId}${partialScoreIdPart}`,
  );
  return response;
}

export const getAnnotations = async (
  examTaskGroupSolutionScoreId: string,
  examTaskGroupSolutionScoreVersionId: string | undefined,
): Promise<AxiosResponse<AnnotationsForORGZ>> => {
  const examTaskGroupSolutionScoreVersionIdPart = examTaskGroupSolutionScoreVersionId
    ? `?examTaskGroupSolutionScoreVersionId=${examTaskGroupSolutionScoreVersionId}`
    : '';
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/Annotations${examTaskGroupSolutionScoreVersionIdPart}`,
  );
  return response;
};

export const getAnnotationVersions = async (
  examTaskGroupSolutionScoreId: string,
): Promise<AxiosResponse<AnnotationVersionsForORGZ>> => {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/AnnotationVersions`,
  );
  return response;
};

export async function addAnnotationOnOrgz(
  partialScoreId: string,
  annotation: AnnotationOnImage,
): Promise<AxiosResponse<string>> {
  const response = axios.post(
    EnvWindow.REACT_APP_SCORING__ANNOTATION_API_URL,
    {
      partialScoreId,
      annotationDefinitionId: annotation.annotationDefinitionId,
      positionX: Math.round(annotation.positionX),
      positionY: Math.round(annotation.positionY),
      pageNumber: annotation.pageNumber,
      width: annotation.width !== null ? Math.round(annotation.width) : null,
      height: annotation.height !== null ? Math.round(annotation.height) : null,
      angle: annotation.angle !== null ? Math.round(annotation.angle) : null,
      layer: annotation.layer,
    },
    {
      // transformes response to string in order to prevent returning it as number which is rounded
      transformResponse: [
        function (data: string) {
          return data;
        },
      ],
    },
  );
  return response;
}

export async function updateAnnotationOnOrgz(annotations: AnnotationOnImage[]): Promise<AxiosResponse<boolean>> {
  const data = annotations.map((annotation) => ({
    positionX: Math.round(annotation.positionX),
    positionY: Math.round(annotation.positionY),
    pageNumber: annotation.pageNumber,
    width: annotation.width !== null ? Math.round(annotation.width) : null,
    height: annotation.height !== null ? Math.round(annotation.height) : null,
    angle: annotation.angle !== null ? Math.round(annotation.angle) : null,
    layer: annotation.layer,
    id: annotation.annotationId,
  }));
  const response = axios.patch(EnvWindow.REACT_APP_SCORING__ANNOTATION_API_URL, data);
  return response;
}

export const getCriterionAnnotation = async (
  examScoringSchemaId: string,
  orgzId: string | undefined,
): Promise<AxiosResponse<AnnotationsCriterionDTO>> => {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_SCORING_API_URL}/ExamScoringSchema/${examScoringSchemaId}/ExaminerAnnotation/${orgzId}`,
  );
  return response;
};

export const setCriterionAnnotationSequence = async (
  examScoringSchemaId: string,
  sequence: string[] | undefined,
): Promise<AxiosResponse<AnnotationsCriterionDTO>> => {
  const response = await axios.patch(
    `${EnvWindow.REACT_APP_SCORING_API_URL}/ExamScoringSchema/${examScoringSchemaId}/ExaminerAnnotation`,
    sequence,
  );
  return response;
};

export async function deleteAnnotationOnOrgz(annotationId: string | string[]): Promise<AxiosResponse<boolean>> {
  if (Array.isArray(annotationId)) {
    const response = await axios.all(
      annotationId.map((id) => axios.delete(`${EnvWindow.REACT_APP_SCORING__ANNOTATION_API_URL}/${id}`)),
    );
    return response[0];
  } else {
    const response = axios.delete(`${EnvWindow.REACT_APP_SCORING__ANNOTATION_API_URL}/${annotationId}`);
    return response;
  }
}

export const createExamTaskGroupSolutionScoreShelf = async (
  examTaskGroupSolutionScoreId: string,
): Promise<AxiosResponse<ExamTaskGroupSolutionScoreShelfResponse>> => {
  const response = axios.post(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/shelf`,
  );
  return response;
};

export async function sendBackAssessedSolutionsShelf(
  examTaskGroupSolutionScoreId: string,
  examTaskGroupSolutionScoreVersionId: string,
): Promise<AxiosResponse<SendBackAssessedExamTaskGroupSolutionsResponse>> {
  return await axios.patch(
    `${EnvWindow.REACT_APP_SCORING__EXAM_TASK_GROUP_SOLUTION_SCORE_API_URL}/${examTaskGroupSolutionScoreId}/latest/${examTaskGroupSolutionScoreVersionId}`,
  );
}
